/** Columna del tablero kanban con su página actual, su total y si está cargando. */
export type ColumnaTablero<TColumna, TItem> = TColumna & {
  items: TItem[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
  loading: boolean;
};

/** Columnas del tablero en su estado inicial: página 1 y sin registros. */
export function crearColumnasTablero<TColumna, TItem>(columnas: TColumna[]): ColumnaTablero<TColumna, TItem>[] {
  return columnas.map((column) => ({
    ...column,
    items: [],
    currentPage: 1,
    totalItems: 0,
    totalPages: 0,
    loading: false,
  }));
}

/**
 * Tablero kanban por estado de las incidencias IA, de seguridad y de Hosting: registros por página,
 * filtros, total y paginación de cada columna. Cada pantalla dice cómo consulta una columna.
 */
export abstract class TableroIncidenciasBase<TColumna extends { estado: string }, TItem extends object> {
  readonly pageSizeOptions = [5, 10, 25, 100];
  abstract readonly columns: ColumnaTablero<TColumna, TItem>[];
  filtros = {} as TItem;
  itemsPerPage = 5;
  filtersPopupOpen = false;
  errorCarga = "";

  get totalItems(): number {
    return this.columns.reduce((acc, column) => acc + column.totalItems, 0);
  }

  /** Consulta la página actual de la columna con los filtros del tablero. */
  abstract consultarColumna(column: ColumnaTablero<TColumna, TItem>): void;

  trackByEstado(_index: number, column: ColumnaTablero<TColumna, TItem>): TColumna["estado"] {
    return column.estado;
  }

  refrescarTodas(): void {
    this.columns.forEach((column) => this.consultarColumna(column));
  }

  onItemsPerPageChange(value: number | string): void {
    this.itemsPerPage = Number(value) || 5;
    this.reiniciarPaginas();
    this.refrescarTodas();
  }

  aplicarFiltros(): void {
    this.reiniciarPaginas();
    this.refrescarTodas();
    this.filtersPopupOpen = false;
  }

  limpiarFiltros(): void {
    this.filtros = {} as TItem;
    this.aplicarFiltros();
  }

  pageChanged(column: ColumnaTablero<TColumna, TItem>, page: number): void {
    if (page < 1 || page > column.totalPages || page === column.currentPage) {
      return;
    }
    column.currentPage = page;
    this.consultarColumna(column);
  }

  firstPage(column: ColumnaTablero<TColumna, TItem>): void {
    this.pageChanged(column, 1);
  }

  previousPage(column: ColumnaTablero<TColumna, TItem>): void {
    this.pageChanged(column, column.currentPage - 1);
  }

  nextPage(column: ColumnaTablero<TColumna, TItem>): void {
    this.pageChanged(column, column.currentPage + 1);
  }

  lastPage(column: ColumnaTablero<TColumna, TItem>): void {
    this.pageChanged(column, column.totalPages);
  }

  getPaginationItems(column: ColumnaTablero<TColumna, TItem>): Array<number | string> {
    if (column.totalPages <= 1) {
      return [1];
    }
    const pages = new Set<number>([
      1,
      column.totalPages,
      column.currentPage - 1,
      column.currentPage,
      column.currentPage + 1,
    ]);
    const sorted = Array.from(pages)
      .filter((page) => page >= 1 && page <= column.totalPages)
      .sort((a, b) => a - b);
    const items: Array<number | string> = [];
    sorted.forEach((page, index) => {
      const previous = sorted[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    });
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  formatDate(value?: string | Date): string {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /** Regresa cada columna a su primera página. */
  protected reiniciarPaginas(): void {
    this.columns.forEach((column) => {
      column.currentPage = 1;
    });
  }
}
